<template>
  <section class="lms-instructions" :class="{ 'is-scrollable': scrollable }">
    <button
      type="button"
      class="lms-instructions__toggle"
      :aria-expanded="open ? 'true' : 'false'"
      @click="open = !open"
    >
      <span>
        <span class="lms-instructions__eyebrow">Guide</span>
        <span class="lms-instructions__title">{{ title }}</span>
      </span>
      <v-icon
        :icon="open ? 'mdi-chevron-up' : 'mdi-chevron-down'"
        size="20"
      />
    </button>

    <div
      v-if="scrollable"
      v-show="open"
      class="lms-instructions__body-wrap"
    >
      <div class="lms-instructions__body" v-html="bodyHtml" />
    </div>
    <v-expand-transition v-else>
      <div v-show="open" class="lms-instructions__body-wrap">
        <div class="lms-instructions__body" v-html="bodyHtml" />
      </div>
    </v-expand-transition>
  </section>
</template>

<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  title: { type: String, default: 'Instructions' },
  html: { type: String, default: '' },
  // Classic Bootstrap panels use .collapse (closed) unless .in is present.
  defaultOpen: { type: Boolean, default: false },
  scrollable: { type: Boolean, default: false }
})

const open = ref(props.defaultOpen)

const bodyHtml = computed(() => {
  if (typeof DOMParser === 'undefined') return props.html

  const document = new DOMParser().parseFromString(props.html, 'text/html')
  const body = document.querySelector('.panel-body')?.innerHTML.trim()
  if (body) return body

  // Some legacy instruction partials place the list beside an empty panel body.
  // Keep the collapsible content without carrying its duplicate heading forward.
  return document.querySelector('.panel-collapse')?.innerHTML || props.html
})
</script>

<style scoped>
.lms-instructions {
  overflow: hidden;
  border: 1px solid var(--lms-border);
  border-radius: 14px;
  background: var(--app-surface-raised);
  box-shadow: 0 8px 22px rgba(10, 79, 108, 0.06);
}

.lms-instructions.is-scrollable {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
}

.lms-instructions__toggle {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  border: 0;
  background: color-mix(in srgb, var(--lms-primary) 8%, var(--app-surface));
  color: var(--lms-text);
  text-align: left;
  cursor: pointer;
  flex-shrink: 0;
}

.lms-instructions__toggle:hover {
  background: color-mix(in srgb, var(--lms-primary) 13%, var(--app-surface));
}

.lms-instructions__eyebrow,
.lms-instructions__title {
  display: block;
}

.lms-instructions__eyebrow {
  margin-bottom: 2px;
  color: var(--lms-primary);
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.lms-instructions__title {
  font-size: 1rem;
  font-weight: 700;
}

.lms-instructions__body-wrap {
  min-height: 0;
}

.lms-instructions.is-scrollable .lms-instructions__body-wrap {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.lms-instructions__body {
  padding: 16px 20px 18px;
  color: var(--lms-text);
  font-size: 0.9rem;
  line-height: 1.55;
}

.lms-instructions.is-scrollable .lms-instructions__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.lms-instructions__body :deep(ol) {
  margin: 0;
  padding-left: 1.35rem;
}

.lms-instructions__body :deep(ol ol),
.lms-instructions__body :deep(ul) {
  margin-top: 6px;
  padding-left: 1.35rem;
}

.lms-instructions__body :deep(li + li) {
  margin-top: 8px;
}

.lms-instructions__body :deep(strong) {
  color: var(--lms-primary);
}
</style>
